import React from 'react';
import type { Address, ValidationErrors } from '../../../types/wizard';
import { US_STATES } from '@shared/constants/us-data';

interface AddressFormProps {
  values: Address;
  onChange: (data: Partial<Address>) => void;
  errors: ValidationErrors;
  /** Optional prefix for field IDs to avoid collisions when rendering multiple address forms */
  idPrefix?: string;
}

const AddressForm: React.FC<Readonly<AddressFormProps>> = ({ values, onChange, errors, idPrefix = '' }) => {
  const id = (name: string) => `${idPrefix}${name}`;

  return (
    <>
      <div className="form-group">
        <label htmlFor={id('address1')}>
          Address Line 1 <span className="required">*</span>
        </label>
        <input
          type="text"
          id={id('address1')}
          value={values.address1}
          onChange={(e) => onChange({ address1: e.target.value })}
          className={errors.address1 ? 'error' : ''}
          placeholder="Enter street address"
        />
        {errors.address1 && (
          <div className="error-text">{errors.address1}</div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor={id('address2')}>Address Line 2</label>
        <input
          type="text"
          id={id('address2')}
          value={values.address2 || ''}
          onChange={(e) => onChange({ address2: e.target.value })}
          placeholder="Apartment, suite, etc. (optional)"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor={id('city')}>
            City <span className="required">*</span>
          </label>
          <input
            type="text"
            id={id('city')}
            value={values.city}
            onChange={(e) => onChange({ city: e.target.value })}
            className={errors.city ? 'error' : ''}
            placeholder="Enter city"
          />
          {errors.city && (
            <div className="error-text">{errors.city}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor={id('state')}>
            State <span className="required">*</span>
          </label>
          <select
            id={id('state')}
            value={values.state}
            onChange={(e) => onChange({ state: e.target.value })}
            className={errors.state ? 'error' : ''}
          >
            <option value="">Select State</option>
            {US_STATES.map((stateOption) => (
              <option key={stateOption.value} value={stateOption.value}>
                {stateOption.label}
              </option>
            ))}
          </select>
          {errors.state && (
            <div className="error-text">{errors.state}</div>
          )}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor={id('zip')}>
            ZIP Code <span className="required">*</span>
          </label>
          <input
            type="text"
            id={id('zip')}
            value={values.zip}
            onChange={(e) => onChange({ zip: e.target.value })}
            className={errors.zip ? 'error' : ''}
            placeholder="12345"
            maxLength={10}
          />
          {errors.zip && (
            <div className="error-text">{errors.zip}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor={id('country')}>Country</label>
          <input
            type="text"
            id={id('country')}
            value={values.country}
            readOnly
            disabled
          />
        </div>
      </div>
    </>
  );
};

export default AddressForm;
